import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FolderPlus, Layers, Plus, ArrowRight, Coins } from "lucide-react";
import { apiClient } from "@/lib/api";
import { useCollections } from "@/context/CollectionContext";
import { CategoryThumb } from "@/components/CategoryPill";

/**
 * CollectionBuilder — guided wizard:
 *   1. Pick / create a collection
 *   2. Pick / create a category
 *   3. Routes to /items/new?collection_id=...&category_id=...
 */
export default function CollectionBuilder() {
  const navigate = useNavigate();
  const { collections, refresh: refreshCollections, setActive } = useCollections();
  const [step, setStep] = useState(1);
  const [collectionId, setCollectionId] = useState("");
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // New-collection form
  const [creatingCollection, setCreatingCollection] = useState(false);
  const [newCol, setNewCol] = useState({ name: "", description: "", currency: "NZD", currency_symbol: "$" });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/categories");
        setCategories(data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const createCollection = async () => {
    if (!newCol.name.trim()) { toast.error("Collection name is required."); return; }
    setCreatingCollection(true);
    try {
      const { data } = await apiClient.post("/collections", newCol);
      toast.success("Collection created.");
      await refreshCollections();
      setActive(data.id);
      setCollectionId(data.id);
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create collection.");
    } finally {
      setCreatingCollection(false);
    }
  };

  const pickExistingCollection = (id) => {
    setCollectionId(id);
    setActive(id);
    setStep(2);
  };

  const pickCategory = (catId) => {
    const qs = new URLSearchParams();
    if (collectionId) qs.set("collection_id", collectionId);
    qs.set("category_id", catId);
    navigate(`/items/new?${qs.toString()}`);
  };

  const createNewCategory = () => {
    const next = collectionId
      ? `/items/new?collection_id=${collectionId}`
      : `/items/new`;
    navigate(`/categories/new?next=${encodeURIComponent(next)}`);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="collection-builder">
      <Link to="/" data-testid="cob-back" className="vp-btn vp-btn-ghost vp-btn-sm mb-4">
        <ArrowLeft size={14} /> Back Home
      </Link>

      <div className="vp-label mb-2">Collection Builder</div>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-2">Build your collection</h1>
      <p className="text-sm vp-muted mb-8 max-w-xl">Pick or create a collection, then choose a category. You'll be taken straight to Item Entry.</p>

      <div className="flex items-center gap-3 mb-6 text-xs vp-muted" data-testid="cob-stepper">
        <StepDot n={1} label="Collection" active={step >= 1} done={step > 1} />
        <span className="h-px w-8 bg-[hsl(var(--border))]" />
        <StepDot n={2} label="Category" active={step >= 2} done={false} />
        <span className="h-px w-8 bg-[hsl(var(--border))]" />
        <StepDot n={3} label="Add Items" active={false} done={false} />
      </div>

      {step === 1 && (
        <div className="space-y-6" data-testid="cob-step-1">
          <section className="vp-card p-6">
            <h2 className="text-lg font-semibold mb-1">Choose a collection</h2>
            <p className="text-xs vp-muted mb-5">Items in a collection share the same currency.</p>
            {collections.length === 0 ? (
              <div className="vp-muted text-sm py-6 text-center">No collections yet — create one below.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {collections.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => pickExistingCollection(c.id)}
                    data-testid={`cob-pick-${c.id}`}
                    className="text-left vp-card vp-card-hover p-4 group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[hsl(var(--accent))] text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))] flex items-center justify-center">
                        <Layers size={16} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{c.name}</div>
                        <div className="text-xs vp-muted">{c.currency_symbol || "$"} {c.currency || "NZD"}</div>
                      </div>
                      <ArrowRight size={14} className="vp-muted group-hover:translate-x-0.5 transition-transform duration-fast" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section className="vp-card p-6">
            <h2 className="text-lg font-semibold mb-1">Or create a new collection</h2>
            <p className="text-xs vp-muted mb-5">Give it a name and pick a currency.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium mb-1.5 vp-muted">Name</label>
                <input
                  data-testid="cob-newcol-name"
                  value={newCol.name}
                  onChange={(e) => setNewCol({ ...newCol, name: e.target.value })}
                  className="vp-input"
                  placeholder="e.g. Vintage Watches"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium mb-1.5 vp-muted">Description</label>
                <input
                  data-testid="cob-newcol-desc"
                  value={newCol.description}
                  onChange={(e) => setNewCol({ ...newCol, description: e.target.value })}
                  className="vp-input"
                  placeholder="Short description (optional)"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 vp-muted">Currency code</label>
                <input
                  data-testid="cob-newcol-currency"
                  value={newCol.currency}
                  onChange={(e) => setNewCol({ ...newCol, currency: e.target.value.toUpperCase().slice(0, 3) })}
                  className="vp-input"
                  placeholder="NZD"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5 vp-muted">Currency symbol</label>
                <input
                  data-testid="cob-newcol-symbol"
                  value={newCol.currency_symbol}
                  onChange={(e) => setNewCol({ ...newCol, currency_symbol: e.target.value.slice(0, 3) })}
                  className="vp-input"
                  placeholder="$"
                />
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={createCollection}
                disabled={creatingCollection}
                data-testid="cob-newcol-create"
                className="vp-btn vp-btn-primary vp-btn-sm disabled:opacity-50"
              >
                <FolderPlus size={14} /> {creatingCollection ? "Creating…" : "Create & Continue"}
              </button>
            </div>
          </section>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-6" data-testid="cob-step-2">
          <section className="vp-card p-6">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-semibold">Choose a category</h2>
              <button onClick={() => setStep(1)} className="text-xs vp-muted hover:text-[hsl(var(--text))]">← Change collection</button>
            </div>
            <p className="text-xs vp-muted mb-5">Pick the type of item you're cataloguing.</p>
            {loading ? (
              <div className="vp-skeleton h-20 rounded-md" />
            ) : categories.length === 0 ? (
              <div className="vp-muted text-sm py-6 text-center">No categories yet. Create your first one below.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categories.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => pickCategory(c.id)}
                    data-testid={`cob-cat-${c.id}`}
                    className="text-left vp-card vp-card-hover p-4 group"
                  >
                    <div className="flex items-center gap-3">
                      <CategoryThumb image={c.image} size="md" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{c.category_name}</div>
                        <div className="text-xs vp-muted truncate">{c.category_description || "No description"}</div>
                      </div>
                      <ArrowRight size={14} className="vp-muted group-hover:translate-x-0.5 transition-transform duration-fast" />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </section>

          <button
            onClick={createNewCategory}
            data-testid="cob-create-cat"
            className="w-full vp-card vp-card-hover p-5 flex items-center justify-center gap-2 text-sm font-medium text-[hsl(var(--gold-strong))] dark:text-[hsl(var(--gold))]"
          >
            <Plus size={15} /> Create New Category
          </button>
        </div>
      )}
    </div>
  );
}

const StepDot = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
      done ? "bg-[hsl(var(--gold))] text-[#1a1a1a]" :
      active ? "bg-[hsl(var(--gold))] text-[#1a1a1a]" :
      "bg-[hsl(var(--muted))] vp-muted"
    }`}>{n}</div>
    <span className={active ? "" : "vp-muted"}>{label}</span>
  </div>
);

// Expose Coins icon as a stable named-export to avoid tree-shaking warnings
export { Coins };
